import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { useLanguage } from '../i18n';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Package, CheckCircle, Truck, Box } from 'lucide-react';

export default function OperationsDashboard() {
  const { t, getProductName } = useLanguage();
  const [mondayOrders, setMondayOrders] = useState([]);
  const [thursdayOrders, setThursdayOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('monday');
  const [actualQuantities, setActualQuantities] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mondayRes, thursdayRes] = await Promise.all([
        axios.get(`${API}/production/monday`),
        axios.get(`${API}/production/thursday`),
      ]);
      setMondayOrders(mondayRes.data);
      setThursdayOrders(thursdayRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status?new_status=${newStatus}`);
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const calculateProductTotals = (orders) => {
    const totals = {};
    orders.forEach(order => {
      order.order_items?.forEach(item => {
        const name = item.product?.product_name_ko || 'Unknown';
        if (!totals[name]) totals[name] = { quantity: 0, unit: item.product?.unit_type };
        totals[name].quantity += item.ordered_quantity;
      });
    });
    return totals;
  };

  const renderProductionView = (orders) => {
    const totals = calculateProductTotals(orders);
    return (
      <div className="space-y-6">
        {/* Production Totals - Big Numbers for tablet */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Box className="h-5 w-5 text-[#556B2F]" />
            {t('productionTotals')}
          </h3>
          {Object.keys(totals).length === 0 ? (
            <p className="text-center py-8 text-gray-500">No orders scheduled</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(totals).map(([name, info]) => (
                <div key={name} className="bg-gray-50 border border-gray-200 rounded-md p-5" data-testid={`production-total-${name}`}>
                  <p className="text-sm text-gray-600 mb-1">{name}</p>
                  <p className="text-4xl font-bold text-gray-900 cff-text-numeric">
                    {info.quantity}
                    <span className="text-base font-normal text-gray-500 ml-2">
                      {info.unit === 'box' ? t('boxes') : t('packs')}
                    </span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Orders by Customer */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('orders')}</h3>
          {orders.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No orders</p>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="border border-gray-200 rounded-md p-4" data-testid={`ops-order-${order.order_number}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                    <div>
                      <p className="font-mono text-sm text-gray-500">{order.order_number}</p>
                      <p className="font-semibold text-gray-900">{order.company?.company_name}</p>
                      <p className="text-sm text-gray-600">{order.delivery_location?.location_name}</p>
                    </div>
                    <span className={`status-badge status-${order.status.replace('_', '-')} self-start`}>
                      {order.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-3">
                    {order.order_items?.map(item => (
                      <div key={item.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{getProductName(item.product)}</span>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold cff-text-numeric">{item.ordered_quantity}</span>
                          <Input
                            type="number"
                            placeholder={t('actualShipped')}
                            className="w-24 h-9"
                            data-testid={`actual-qty-${item.id}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons - Tablet friendly */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-3 border-t border-gray-100">
                    <Button
                      variant="outline"
                      onClick={() => updateStatus(order.id, 'in_production')}
                      disabled={order.status === 'in_production' || order.status === 'packed' || order.status === 'shipped'}
                      data-testid={`btn-production-${order.order_number}`}
                      className="cff-tablet-button"
                    >
                      <Package className="h-4 w-4 mr-2" /> {t('markAsInProduction')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => updateStatus(order.id, 'packed')}
                      disabled={order.status === 'packed' || order.status === 'shipped'}
                      data-testid={`btn-packed-${order.order_number}`}
                      className="cff-tablet-button"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" /> {t('markAsPacked')}
                    </Button>
                    <Button
                      onClick={() => updateStatus(order.id, 'shipped')}
                      disabled={order.status === 'shipped'}
                      data-testid={`btn-shipped-${order.order_number}`}
                      className="cff-tablet-button cff-button-primary"
                    >
                      <Truck className="h-4 w-4 mr-2" /> {t('markAsShipped')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="operations-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('production')}</h1>
        <p className="text-gray-500 mt-1">Production & shipping management</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-14 w-full sm:w-auto grid grid-cols-2 sm:flex">
          <TabsTrigger value="monday" data-testid="tab-monday" className="h-12 text-base">
            {t('monday')} ({mondayOrders.length})
          </TabsTrigger>
          <TabsTrigger value="thursday" data-testid="tab-thursday" className="h-12 text-base">
            {t('thursday')} ({thursdayOrders.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monday" className="mt-6">
          {renderProductionView(mondayOrders)}
        </TabsContent>
        <TabsContent value="thursday" className="mt-6">
          {renderProductionView(thursdayOrders)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
